import { motion } from 'framer-motion';
import { useLanguage } from '@/i18n/LanguageContext';
import { lc } from '@/i18n/copy';
import { Star } from 'lucide-react';
import reviewBrazil from '@/assets/review-brazil.jpg';
import reviewUsa from '@/assets/review-usa.jpg';
import reviewIndia from '@/assets/review-india.jpg';
import reviewJapan from '@/assets/review-japan.jpg';

const copy = {
  en: {
    heading: 'What Users Are Saying',
    subtitle: 'Real stories from people who stopped waiting.',
    reviews: [
      { name: 'Camila R.', location: 'São Paulo, Brazil', quote: "I wrote a message to my mother that I'd been holding inside for years. She called me crying — in the best way. This gave me the courage I needed." },
      { name: 'Marcus T.', location: 'Atlanta, USA', quote: "My dad and I don't talk about feelings. I sent him something through Say It NOW and he texted me back: 'I needed that more than you know.' Best thing I've ever done." },
      { name: 'Ananya P.', location: 'Mumbai, India', quote: "I used it to tell my best friend how much she meant to me after her divorce. She said it saved her week. Simple, private, and exactly what I needed." },
      { name: 'Kenji H.', location: 'Tokyo, Japan', quote: "My daughter read her announcement three times. She said it was the best gift she ever received. I wish I had done this sooner." },
    ],
  },
  es: {
    heading: 'Lo Que Dicen Nuestros Usuarios',
    subtitle: 'Historias reales de personas que dejaron de esperar.',
    reviews: [
      { name: 'Camila R.', location: 'São Paulo, Brasil', quote: "Le escribí un mensaje a mi madre que había guardado durante años. Me llamó llorando — de la mejor manera. Esto me dio el valor que necesitaba." },
      { name: 'Marcus T.', location: 'Atlanta, EE.UU.', quote: "Mi papá y yo no hablamos de sentimientos. Le envié algo a través de Say It NOW y me respondió: 'Lo necesitaba más de lo que imaginas.' Lo mejor que he hecho." },
      { name: 'Ananya P.', location: 'Bombay, India', quote: "Lo usé para decirle a mi mejor amiga cuánto significaba para mí después de su divorcio. Dijo que le salvó la semana. Simple, privado y justo lo que necesitaba." },
      { name: 'Kenji H.', location: 'Tokio, Japón', quote: "Mi hija leyó su anuncio tres veces. Dijo que fue el mejor regalo que jamás recibió. Ojalá lo hubiera hecho antes." },
    ],
  },
  pt: {
    heading: 'O Que Os Usuários Estão Dizendo',
    subtitle: 'Histórias reais de pessoas que pararam de esperar.',
    reviews: [
      { name: 'Camila R.', location: 'São Paulo, Brasil', quote: "Escrevi uma mensagem para minha mãe que eu guardava há anos. Ela me ligou chorando — da melhor forma. Isso me deu a coragem que eu precisava." },
      { name: 'Marcus T.', location: 'Atlanta, EUA', quote: "Meu pai e eu não falamos sobre sentimentos. Enviei algo pelo Say It NOW e ele me respondeu: 'Eu precisava disso mais do que você imagina.' A melhor coisa que já fiz." },
      { name: 'Ananya P.', location: 'Mumbai, Índia', quote: "Usei para dizer à minha melhor amiga o quanto ela significava para mim após o divórcio. Ela disse que salvou a semana dela. Simples, privado e exatamente o que eu precisava." },
      { name: 'Kenji H.', location: 'Tóquio, Japão', quote: "Minha filha leu o anúncio três vezes. Ela disse que foi o melhor presente que já recebeu. Queria ter feito isso antes." },
    ],
  },
  fr: {
    heading: 'Ce Que Disent Nos Utilisateurs',
    subtitle: 'Des histoires vraies de gens qui ont arrêté d\'attendre.',
    reviews: [
      { name: 'Camila R.', location: 'São Paulo, Brésil', quote: "J'ai écrit un message à ma mère que je gardais en moi depuis des années. Elle m'a appelée en pleurant — de la plus belle façon. Ça m'a donné le courage dont j'avais besoin." },
      { name: 'Marcus T.', location: 'Atlanta, États-Unis', quote: "Mon père et moi ne parlons pas de sentiments. Je lui ai envoyé quelque chose via Say It NOW et il m'a répondu : 'J'en avais besoin plus que tu ne le crois.' La meilleure chose que j'aie jamais faite." },
      { name: 'Ananya P.', location: 'Mumbai, Inde', quote: "Je l'ai utilisé pour dire à ma meilleure amie combien elle comptait pour moi après son divorce. Elle a dit que ça avait sauvé sa semaine. Simple, privé et exactement ce dont j'avais besoin." },
      { name: 'Kenji H.', location: 'Tokyo, Japon', quote: "Ma fille a lu son annonce trois fois. Elle a dit que c'était le plus beau cadeau qu'elle ait jamais reçu. J'aurais aimé le faire plus tôt." },
    ],
  },
  ar: {
    heading: 'ماذا يقول المستخدمون',
    subtitle: 'قصص حقيقية من أشخاص توقفوا عن الانتظار.',
    reviews: [
      { name: 'كاميلا ر.', location: 'ساو باولو، البرازيل', quote: "كتبت رسالة لأمي كنت أحتفظ بها بداخلي لسنوات. اتصلت بي وهي تبكي — بأجمل طريقة. هذا منحني الشجاعة التي كنت أحتاجها." },
      { name: 'ماركوس ت.', location: 'أتلانتا، الولايات المتحدة', quote: "أبي وأنا لا نتحدث عن المشاعر. أرسلت له شيئاً عبر Say It NOW فأرسل لي: 'كنت أحتاج هذا أكثر مما تتخيل.' أفضل شيء فعلته في حياتي." },
      { name: 'أنانيا ب.', location: 'مومباي، الهند', quote: "استخدمته لأخبر صديقتي المقربة كم تعني لي بعد طلاقها. قالت إنه أنقذ أسبوعها. بسيط وخاص وبالضبط ما كنت أحتاجه." },
      { name: 'كينجي هـ.', location: 'طوكيو، اليابان', quote: "ابنتي قرأت إعلانها ثلاث مرات. قالت إنه أفضل هدية تلقتها على الإطلاق. أتمنى لو فعلت هذا من قبل." },
    ],
  },
  ja: {
    heading: 'ユーザーの声',
    subtitle: '待つのをやめた人々の実話。',
    reviews: [
      { name: 'カミラ R.', location: 'サンパウロ、ブラジル', quote: "何年も心に秘めていた言葉を母に書きました。母は泣きながら電話をくれました——最高の涙でした。必要だった勇気をもらえました。" },
      { name: 'マーカス T.', location: 'アトランタ、アメリカ', quote: "父と感情の話はしません。Say It NOWで何かを送ったら、父から「お前が思っている以上に必要だった」と返信が来ました。今までで最高のことでした。" },
      { name: 'アナンヤ P.', location: 'ムンバイ、インド', quote: "離婚後の親友にどれほど大切かを伝えるために使いました。彼女は「一週間が救われた」と言ってくれました。シンプルでプライベートで、まさに必要なものでした。" },
      { name: 'ケンジ H.', location: '東京、日本', quote: "娘はアナウンスメントを3回読みました。今まで受け取った最高のプレゼントだと言いました。もっと早くやればよかったです。" },
    ],
  },
  ko: {
    heading: '사용자들의 이야기',
    subtitle: '기다리기를 멈춘 사람들의 실제 이야기.',
    reviews: [
      { name: '카밀라 R.', location: '상파울루, 브라질', quote: "수년간 마음속에 담아두었던 메시지를 어머니에게 썼습니다. 어머니는 울면서 전화하셨어요 — 가장 좋은 의미로요. 필요했던 용기를 얻었습니다." },
      { name: '마커스 T.', location: '애틀랜타, 미국', quote: "아버지와 저는 감정에 대해 이야기하지 않습니다. Say It NOW를 통해 보냈더니 '네가 생각하는 것보다 더 필요했다'라고 답장이 왔습니다. 제가 한 가장 좋은 일이었어요." },
      { name: '아난야 P.', location: '뭄바이, 인도', quote: "이혼 후 가장 친한 친구에게 얼마나 소중한지 전하기 위해 사용했습니다. 친구는 한 주를 구해줬다고 했어요. 간단하고 프라이빗하며 정확히 필요한 것이었습니다." },
      { name: '켄지 H.', location: '도쿄, 일본', quote: "딸이 발표를 세 번 읽었습니다. 지금까지 받은 최고의 선물이라고 했어요. 더 일찍 했으면 좋았을 텐데요." },
    ],
  },
  id: {
    heading: 'Apa Kata Pengguna',
    subtitle: 'Kisah nyata dari orang-orang yang berhenti menunggu.',
    reviews: [
      { name: 'Camila R.', location: 'São Paulo, Brasil', quote: "Saya menulis pesan untuk ibu saya yang sudah saya simpan bertahun-tahun. Beliau menelepon sambil menangis — dengan cara terbaik. Ini memberi saya keberanian yang saya butuhkan." },
      { name: 'Marcus T.', location: 'Atlanta, AS', quote: "Ayah dan saya tidak pernah bicara tentang perasaan. Saya mengirim sesuatu lewat Say It NOW dan dia membalas: 'Aku membutuhkan itu lebih dari yang kau tahu.' Hal terbaik yang pernah saya lakukan." },
      { name: 'Ananya P.', location: 'Mumbai, India', quote: "Saya menggunakannya untuk memberi tahu sahabat saya betapa berartinya dia setelah perceraiannya. Dia bilang itu menyelamatkan minggunya. Sederhana, pribadi, dan tepat yang saya butuhkan." },
      { name: 'Kenji H.', location: 'Tokyo, Jepang', quote: "Putri saya membaca pengumumannya tiga kali. Dia bilang itu hadiah terbaik yang pernah dia terima. Saya berharap sudah melakukan ini lebih awal." },
    ],
  },

  hi: {
    heading: 'उपयोगकर्ता क्या कह रहे हैं',
    subtitle: 'उन लोगों की सच्ची कहानियाँ जिन्होंने इंतजार करना बंद कर दिया।',
    reviews: [
      { name: 'Camila R.', location: 'São Paulo, Brazil', quote: "I wrote a message to my mother that I'd been holding inside for years. She called me crying — in the best way. This gave me the courage I needed." },
      { name: 'Marcus T.', location: 'Atlanta, USA', quote: "My dad and I don't talk about feelings. I sent him something through Say It NOW and he texted me back: 'मुझे उसकी जितनी ज़रूरत थी, उससे कहीं ज़्यादा।' Best thing I've ever done." },
      { name: 'Ananya P.', location: 'Mumbai, India', quote: "I used it to tell my best friend how much she meant to me after her divorce. She said it saved her week. Simple, private, and exactly what I needed." },
      { name: 'केंजी एच.', location: 'टोक्यो, जापान', quote: "My daughter read her announcement three times. She said it was the best gift she ever received. I wish I had done this sooner." },
    ],
  },
};

const photos = [reviewBrazil, reviewUsa, reviewIndia, reviewJapan];
const flags = ['🇧🇷', '🇺🇸', '🇮🇳', '🇯🇵'];

export function SocialProofMetrics() {
  const { language } = useLanguage();
  const t = lc(copy, language);

  return (
    <section className="py-20 md:py-28 bg-gradient-to-b from-background via-muted/30 to-background">
      <div className="container max-w-2xl mx-auto px-4 sm:px-6">
        {/* Header */}
        <motion.div
          className="text-center mb-12"
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.6 }}
        >
          <h2 className="text-3xl md:text-4xl font-heading font-bold billboard-flash mb-3 !leading-tight">
            {t.heading}
          </h2>
          <p className="text-muted-foreground text-base md:text-lg">
            {t.subtitle}
          </p>
        </motion.div>

        {/* Stacked Review Cards */}
        <div className="flex flex-col gap-6">
          {t.reviews.map((review, i) => (
            <motion.div
              key={i}
              className="rounded-2xl border border-border/60 bg-card p-6 md:p-8 shadow-elevation-1"
              initial={{ opacity: 0, y: 24 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ delay: i * 0.12, duration: 0.5 }}
            >
              {/* Stars */}
              <div className="flex gap-0.5 mb-4">
                {[...Array(5)].map((_, s) => (
                  <Star key={s} className="h-5 w-5 text-secondary" fill="currentColor" />
                ))}
              </div>

              {/* Quote */}
              <p className="text-foreground text-base md:text-lg leading-relaxed mb-6">
                &ldquo;{review.quote}&rdquo;
              </p>

              {/* Author */}
              <div className="flex items-center gap-3">
                <img
                  src={photos[i]}
                  alt={review.name}
                  className="h-12 w-12 rounded-full object-cover border-2 border-primary/20"
                />
                <div>
                  <p className="text-sm font-semibold text-foreground flex items-center gap-2">
                    {review.name}
                    <span className="text-base leading-none">{flags[i]}</span>
                  </p>
                  <p className="text-xs text-muted-foreground">{review.location}</p>
                </div>
              </div>
            </motion.div>
          ))}
        </div>
      </div>
    </section>
  );
}
